/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DataGridPro,
  DataGridProProps,
  GridColDef,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import {
  InternalMessageRequest,
  MarkMessageRead,
  Message,
  RecalledMessageDto,
} from "../../../types/Message";
import {
  getInternalMessages,
  getInternalMessagesUnreadCount,
  markMessageAsRead,
  recallMessage,
} from "../../../services/messageService";
import useApi from "../../../hooks/useApi";
import { useSettingValue } from "../../../services/useSettingValue";
import {
  dataGridHeaderHeight,
  ENTITYTYPE,
  GLOBALSETTINGS,
  GRIDCOLUMNKEYS,
  ModuleNames,
  SEVERITIES,
  SYSTEMSETTINGS,
} from "../../../data/constants";
import { useUser } from "../../../context/UserContext";
import { filterDataBySearch, handleScrollPositionChange } from "../../../utils/commonHelper";
import {
  ACTIONITEMVALIDATIONS,
  GLOBALMESSAGES,
  INTERNALMESSAGES,
  MESSAGEREAD_FAIL,
} from "../../../constants/messages";
import { Box, Tooltip, Typography } from "@mui/material";
import CustomLoadingOverlay from "../../../components/ui/CustomLoadingOverlay";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import MarkAsUnreadIcon from "@mui/icons-material/MarkAsUnread";
import { useToaster } from "../../../hooks/useToaster";
import "./Messages.scss";
import ReplyMessage from "./ReplyMessage";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { MessagePriority } from "../../../data/Enums";
import { getCurrentESTTime, getEstDateBySameDayFormat } from "../../../utils/dateHelper";
import { useDispatch } from "react-redux";
import { setCount } from "../../../redux/messageCountSlice";
import { useSignalR } from "../../../hooks/useSignalR";
import { updateTrailingNumber } from "../../../utils/messageHelper";
import { useSignalRConnections } from "../../../context/SignalRProvider";
import { useResizableColumns } from "../../../hooks/useResizableColumns";
import { usePersistedColumnVisibility } from "../../../hooks/usePersistedColumnVisibility";
import ReassignIcon from "../../../components/ui/ReassignIcon";
import UndoIcon from "@mui/icons-material/Undo";
import ReassignMessage from "./ReassignMessage";
import { useMenu } from "../../../context/MenuContext";
import ConfirmationDialog from "../../../components/ui/ConfirmationDialog";

interface InternalMessagesProps {
  type: string;
  searchResult: string;
  refreshKey: number;
}

const InternalMessages: React.FC<InternalMessagesProps> = ({ type, searchResult, refreshKey }) => {
  const { loading, executeApi } = useApi();
  const dispatch = useDispatch();
  const settingValue = useSettingValue(SYSTEMSETTINGS.RESOURCE_PAGINATION);
  const [isPaginationInitialized, setPaginationInitialized] = useState(false);
  const [pageState, setPageState] = useState<{ data: Message[] | null; total: number }>({
    data: null,
    total: 0,
  });
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: parseInt(settingValue ?? GLOBALSETTINGS.DEFAULT_VALUE, 10),
  });
  const [isClientSearchEvaluated, setIsClientSearchEvaluated] = useState(false);
  const [allData, setAllData] = useState<Message[]>([]);
  const isFetching = useRef(false);
  const isFirstLoaded = useRef(false);
  const mounted = useRef(true);
  const apiRef = useGridApiRef();
  const { userId } = useUser();
  const showToaster = useToaster();
  const { permissions } = useMenu();
  const { notificationConnection } = useSignalRConnections();
  const [recallConfirm, setRecallConfirm] = useState(false);
  // Each mailbox has its own column set, so its own saved "Manage columns" selection (bug 78034).
  const { columnVisibilityModel, setColumnVisibilityModel } = usePersistedColumnVisibility(
    `${GRIDCOLUMNKEYS.MESSAGES}-${type}`,
  );
  // The Actions column is permission-driven, not a user preference. MUI fires the change handler
  // with the whole model, so `actions` does end up in the saved copy - the render-time override
  // below is what makes that harmless and is load-bearing: drop it and a stale saved `actions`
  // would outrank the user's current permissions.
  const [showActionsColumn, setShowActionsColumn] = useState(false);
  const permissionsRef = useRef({
    isRecall: false,
    isReassign: false,
    isEdit: false,
  });

  useEffect(() => {
    if (!permissions || permissions.length === 0) return;
    permissionsRef.current.isRecall =
      permissions.find((p) => p.moduleName === ModuleNames.Messages)?.canDelete ?? false;
    permissionsRef.current.isReassign =
      permissions.find((p) => p.moduleName === ModuleNames.Reassign)?.canEdit ?? false;
    permissionsRef.current.isEdit =
      permissions.find((p) => p.moduleName === ModuleNames.Messages)?.canEdit ?? false;

    if (type === ENTITYTYPE.SENT) {
      setShowActionsColumn(permissionsRef.current.isRecall);
    } else if (type === ENTITYTYPE.INBOX) {
      setShowActionsColumn(permissionsRef.current.isEdit || permissionsRef.current.isReassign);
    } else {
      setShowActionsColumn(false);
    }
  }, [permissions, type]);

  React.useEffect(() => {
    apiRef?.current?.subscribeEvent("scrollPositionChange", handleScrollPositionChange);
    return () => {
      mounted.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const receiveMessageHandler = useCallback(
    (toUser: number, message: Message | Message[]) => {
      // Server may send a single message or an array — normalize to single object
      const msg: Message = Array.isArray(message) ? message[0] : message;
      if (!msg) return;

      if (toUser === userId && type === ENTITYTYPE.INBOX) {
        setPageState((prev) => {
          const currentData = prev.data ?? [];

          // Prevent duplicate rows with same messageId
          if (currentData.some((m) => m.messageId === msg.messageId)) {
            return prev;
          }

          // Outlook-style: each message is its own row, simply prepend
          return {
            ...prev,
            data: [msg, ...currentData],
            total: prev.total + 1,
          };
        });
      }
    },
    [userId, type],
  );

  useSignalR(notificationConnection, "ReceiveMessage", receiveMessageHandler);

  const sentMessageHandler = useCallback(
    (fromUser: number, message: Message | Message[]) => {
      // Server may send a single message or an array — normalize to single object
      const msg: Message = Array.isArray(message) ? message[0] : message;
      if (!msg) return;

      if (fromUser === userId && type === ENTITYTYPE.SENT) {
        setPageState((prev) => {
          const currentData = prev.data ?? [];

          // Prevent duplicate rows with same messageId
          if (currentData.some((m) => m.messageId === msg.messageId)) {
            return prev;
          }

          // Sent list shows one row per message — simply prepend
          return {
            ...prev,
            data: [msg, ...currentData],
            total: prev.total + 1,
          };
        });
      }
    },
    [userId, type],
  );

  useSignalR(notificationConnection, "SentMessage", sentMessageHandler);

  const prevPagination = useRef(paginationModel);
  const prevSearchResult = useRef(searchResult);

  const handleOnRowsScrollEnd: DataGridProProps["onRowsScrollEnd"] = () => {
    if (loading || !pageState?.data) return; // Avoid multiple triggers
    if (isFetching.current) return;

    if (pageState?.data && pageState?.data.length >= pageState?.total) {
      return; // Prevent further API calls when all rows are loaded
    }

    isFetching.current = true;
    setPaginationModel((prev) => {
      const totalPages = Math.ceil(pageState?.total / prev.pageSize);
      if (prev.page + 1 < totalPages) {
        return { ...prev, page: prev.page + 1 };
      }
      return prev;
    });

    // Restore previous scroll position after data updates
    setTimeout(() => {
      isFetching.current = false;
    }, 100);
  };

  useEffect(() => {
    if (searchResult) {
      setPaginationModel((prev) => ({
        ...prev,
        page: 0,
      }));
    }
  }, [searchResult]);

  const syncUnreadCount = useCallback(async () => {
    const count = await getInternalMessagesUnreadCount();
    if (count !== null && count !== undefined) {
      dispatch(setCount(count));
    }
  }, [dispatch]);

  const markAsRead = async (msg: Message, checked: boolean) => {
    // Without a recipientID the update matches no row server-side, so fail fast
    // instead of sending 0 and reporting a generic error after a wasted round trip.
    if (!msg.recipientID) {
      showToaster(MESSAGEREAD_FAIL(checked), SEVERITIES.ERROR);
      return;
    }
    const markMessageRead: MarkMessageRead = {
      messageId: msg.messageId,
      recipientId: msg.recipientID,
      readVia: msg.readOn,
      markedAsRead: checked,
    };
    const updateRead = await executeApi<MarkMessageRead, boolean>(
      markMessageAsRead,
      markMessageRead,
    );

    if (!updateRead) {
      showToaster(MESSAGEREAD_FAIL(checked), SEVERITIES.ERROR);
    } else {
      setPageState((prev) => {
        if (!prev.data) return prev;
        const newData = prev.data.map((m) =>
          m.messageId === msg.messageId ? { ...m, markedAsRead: checked } : m,
        );
        return { ...prev, data: newData };
      });
      // Always sync count from server — local increment/decrement is unreliable
      // when SignalR delivers multiple messages for the same conversation.
      await syncUnreadCount();
    }
  };

  const handleDiscard = () => {
    setRecallConfirm(false);
  };

  const handleRowClick = async (msg: Message) => {
    if (type !== ENTITYTYPE.RECALL) {
      let messageToOpen = msg;

      if (!msg.markedAsRead && type === ENTITYTYPE.INBOX) {
        // Outlook-style: each row is its own message, just mark this one as read
        await markAsRead(msg, true);
        messageToOpen = { ...msg, markedAsRead: true };

        // Update the grid row as read
        setPageState((prev) => {
          if (!prev.data) return prev;
          return {
            ...prev,
            data: prev.data.map((m) =>
              m.messageId === msg.messageId ? { ...m, markedAsRead: true } : m,
            ),
          };
        });

        await syncUnreadCount();
      }

      setSelectedMessage(messageToOpen);
      setModalOpen(true);
    }
  };

  const handleRecallConfirm = async (confirmed: boolean) => {
    setRecallConfirm(false);
    if (!confirmed) return;
    const recalledMessageDto: RecalledMessageDto = {
      recipientIds: [selectedMessage.recipientID],
      recalledByUserId: userId,
      isRecalled: true,
      recalledOn: getCurrentESTTime(),
    };
    const recall = await executeApi<RecalledMessageDto, boolean>(recallMessage, recalledMessageDto);

    if (recall) {
      setPageState((prev) => {
        if (!prev.data) return prev;
        const updatedData = prev.data.filter((m) => m.recipientID !== selectedMessage.recipientID);
        return { data: updatedData, total: prev.total - 1 };
      });
      showToaster(INTERNALMESSAGES.RECALL_SUCCESS, SEVERITIES.SUCCESS);
    } else {
      showToaster(INTERNALMESSAGES.RECALL_FAIL, SEVERITIES.ERROR);
    }
  };

  const sentColumns: GridColDef[] = [
    { field: "toCacheName", headerName: "To", flex: 1 },
    { field: "ccCacheName", headerName: "CC", flex: 1 },
    {
      field: "messageSubject",
      headerName: "Subject",
      flex: 2,
      // Bug 78034: Subject stays visible so there is always a column menu to reach "Manage
      // columns" from. Actions is hidden for users without recall rights, so without this the
      // remaining columns could all be hidden - and that now persists across a reload.
      hideable: false,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" height="100%">
          {params.row.messagePriority === MessagePriority.High && (
            <PriorityHighIcon sx={{ color: "red", ml: 1, fontSize: 18 }} titleAccess="High" />
          )}
          {params.row.messagePriority === MessagePriority.Low && (
            <ArrowDownwardIcon sx={{ fontSize: 18 }} titleAccess="Low" />
          )}
          <Typography sx={{ ml: 1 }}>{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "sentOn",
      headerName: "Sent",
      flex: 1,
      valueFormatter: (value) => (value ? getEstDateBySameDayFormat(value) : ""),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.3,
      minWidth: 100,
      sortable: false,
      hideable: false,
      filterable: false,
      disableColumnMenu: false,
      renderCell: (params) => {
        const message = params.row;

        const { isRecall } = permissionsRef.current;

        const handleRecall = async (e: React.MouseEvent) => {
          e.stopPropagation();
          setSelectedMessage(message);
          setRecallConfirm(true);
        };
        return (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={1}
            width="100%"
            height="100%"
          >
            {isRecall ? (
              <>
                <Tooltip title="Recall">
                  <UndoIcon color="error" onClick={handleRecall} sx={{ cursor: "pointer" }} />
                </Tooltip>
              </>
            ) : null}
          </Box>
        );
      },
    },
  ];

  const recalledColumns: GridColDef[] = [
    { field: "recalledUserName", headerName: "Recalled By", flex: 1, hideable: false },
    {
      field: "messageSubject",
      headerName: "Subject",
      flex: 2,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" height="100%">
          {params.row.messagePriority === MessagePriority.High && (
            <PriorityHighIcon sx={{ color: "red", ml: 1, fontSize: 18 }} titleAccess="High" />
          )}
          {params.row.messagePriority === MessagePriority.Low && (
            <ArrowDownwardIcon sx={{ fontSize: 18 }} titleAccess="Low" />
          )}
          <Typography sx={{ ml: 1 }}>{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "recalledOn",
      headerName: "Recalled On",
      flex: 1,
      valueFormatter: (value) => (value ? getEstDateBySameDayFormat(value) : ""),
    },
  ];

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: "cacheFromName",
        headerName: "From",
        flex: 1,
        // Bug 80207: when a message reached this user by reassignment, From must name the
        // user who reassigned it, not the original sender that cacheFromName still holds.
        // Done in valueGetter rather than renderCell alone so that sorting and filtering
        // agree with what is on screen.
        valueGetter: (value, row) => (row as Message).reassignedByUserName || value,
        renderCell: (params) => {
          const { reassignedByUserName, cacheFromName } = params.row as Message;
          return (
            <Box display="flex" alignItems="center" height="100%">
              {reassignedByUserName ? (
                <Tooltip
                  title={`${INTERNALMESSAGES.REASSIGNED_BY} ${reassignedByUserName} • ${INTERNALMESSAGES.ORIGINAL_SENDER}: ${cacheFromName ?? ""}`}
                >
                  <Typography noWrap>{params.value}</Typography>
                </Tooltip>
              ) : (
                <Typography noWrap>{params.value}</Typography>
              )}
            </Box>
          );
        },
      },
      {
        field: "messageSubject",
        headerName: "Subject",
        flex: 2,
        // Bug 78034: see the Sent grid above - Subject stays visible so a column menu is always
        // reachable, since Actions is hidden for users without edit/reassign rights.
        hideable: false,
        renderCell: (params) => (
          <Box display="flex" alignItems="center" height="100%">
            {params.row.messagePriority === MessagePriority.High && (
              <PriorityHighIcon sx={{ color: "red", ml: 1, fontSize: 18 }} titleAccess="High" />
            )}
            {params.row.messagePriority === MessagePriority.Low && (
              <ArrowDownwardIcon sx={{ fontSize: 18 }} titleAccess="Low" />
            )}
            <Typography
              className={params.row.markedAsRead ? "read-row" : "unread-row"}
              sx={{ ml: 1 }}
            >
              {params.value}
            </Typography>
          </Box>
        ),
      },
      {
        field: "sentOn",
        headerName: "Received",
        flex: 1,
        valueFormatter: (value) => (value ? getEstDateBySameDayFormat(value) : ""),
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0.3,
        minWidth: 100,
        sortable: false,
        hideable: false,
        filterable: false,
        disableColumnMenu: false,
        renderCell: (params) => {
          const isRead = params.row.markedAsRead;
          const message = params.row;

          const { isEdit, isReassign } = permissionsRef.current;

          const handleClick = async (e: React.MouseEvent) => {
            e.stopPropagation();
            await markAsRead(message, !isRead);
          };

          const handleReassign = async (e: React.MouseEvent) => {
            e.stopPropagation();
            setSelectedMessage(message);
            setReassignOpen(true);
          };

          return (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={1}
              width="100%"
              height="100%"
            >
              {isEdit &&
                (isRead ? (
                  <Tooltip title="Mark as Unread">
                    <MarkEmailReadIcon
                      color="primary"
                      onClick={handleClick}
                      sx={{ cursor: "pointer" }}
                    />
                  </Tooltip>
                ) : (
                  <Tooltip title="Mark as Read">
                    <MarkAsUnreadIcon
                      color="primary"
                      onClick={handleClick}
                      sx={{ cursor: "pointer" }}
                    />
                  </Tooltip>
                ))}
              {isReassign ? (
                <Tooltip title="Reassign">
                  <ReassignIcon
                    color="secondary"
                    onClick={handleReassign}
                    sx={{ cursor: "pointer" }}
                  />
                </Tooltip>
              ) : null}
            </Box>
          );
        },
      },
    ],
    [],
  );

  const loadData = useCallback(async () => {
    const isNewSearch = paginationModel.page === 0;
    if (isClientSearchEvaluated && isFirstLoaded.current) {
      if (searchResult) {
        const lowerText = searchResult.toLowerCase();
        const filtered = filterDataBySearch(allData, lowerText);
        setPageState({
          data: filtered ?? [],
          total: filtered?.length,
        });
      } else {
        // If no search term, reset to full data
        setPageState({
          data: allData ?? [],
          total: allData?.length,
        });
      }
    } else {
      const auditLogRequest: InternalMessageRequest = {
        userId: userId,
        pageNumber: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        type: type,
        conversationId: null,
      };
      const messagesData = await executeApi<InternalMessageRequest, Message[]>(
        getInternalMessages,
        auditLogRequest,
      );
      if (messagesData && messagesData.length > 0) {
        if (!isFirstLoaded.current && messagesData[0]?.totalRecords <= paginationModel.pageSize) {
          setIsClientSearchEvaluated(true);
          setAllData(messagesData);
        }
        isFirstLoaded.current = true;

        setPageState((prev) => {
          const existingIds = new Set(prev.data?.map((row) => row.messageId)); // Track existing IDs
          const newData = messagesData.filter((row) => !existingIds.has(row.messageId)); // Filter duplicates

          return {
            data: isNewSearch ? messagesData : [...(prev.data || []), ...newData],
            total:
              messagesData.length > 0 && isNewSearch ? messagesData[0].totalRecords : prev.total,
          };
        });
      } else {
        setPageState({
          data: [],
          total: 0,
        });
      }
    }
    setTimeout(() => {
      apiRef?.current?.scrollToIndexes({ rowIndex: apiRef?.current?.getScrollPosition().top });
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel, searchResult]);

  useEffect(() => {
    if (settingValue) {
      const newPageSize = parseInt(settingValue ?? GLOBALSETTINGS.DEFAULT_VALUE, 10);

      setPaginationModel((prev) => {
        if (prev.pageSize !== newPageSize) {
          return { page: 0, pageSize: newPageSize };
        }
        return prev;
      });
      setPaginationInitialized(true);
    }
  }, [settingValue]);

  useEffect(() => {
    if (isPaginationInitialized) {
      setIsClientSearchEvaluated(false);
      isFirstLoaded.current = false;
      if (prevPagination.current.page === 0) {
        loadData();
      } else {
        setPaginationModel((prev) => ({
          ...prev,
          page: 0,
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaginationInitialized]);

  useEffect(() => {
    const hasChanged = JSON.stringify(prevPagination.current) !== JSON.stringify(paginationModel);
    const searchChanged = JSON.stringify(prevSearchResult.current) !== JSON.stringify(searchResult);
    if (hasChanged || searchChanged || refreshKey > 0) {
      prevPagination.current = paginationModel;
      prevSearchResult.current = searchResult;
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel, searchResult, refreshKey]);

  const handleModalClose = async () => {
    setModalOpen(false);
    // Sync count from server in case multiple replies arrived for the conversation.
    if (type === ENTITYTYPE.INBOX) {
      await syncUnreadCount();
    }
  };

  const baseColumns = useMemo(
    () =>
      type === ENTITYTYPE.INBOX
        ? columns
        : type === ENTITYTYPE.SENT
          ? sentColumns
          : recalledColumns,
    [columns, type],
  );

  const { columnState, pinnedColumns, setPinnedColumns, handleColumnResize } =
    useResizableColumns(baseColumns);

  return (
    <Box width="100%">
      <div id="messagesGrid" style={{ height: "600px", width: "100%" }}>
        <DataGridPro
          apiRef={apiRef}
          rows={pageState.data ?? []}
          columns={columnState}
          rowCount={pageState.total}
          getRowClassName={(params) =>
            type === ENTITYTYPE.INBOX ? (params.row.markedAsRead ? "read-row" : "unread-row") : ""
          }
          loading={loading}
          onRowClick={(params) => handleRowClick(params.row as Message)}
          columnHeaderHeight={dataGridHeaderHeight}
          slots={{
            loadingOverlay: CustomLoadingOverlay,
          }}
          paginationModel={paginationModel}
          paginationMode="server"
          getRowId={(row) => row.messageId}
          hideFooterPagination
          localeText={{ noRowsLabel: GLOBALMESSAGES.NOROWSTEXT }}
          onRowsScrollEnd={handleOnRowsScrollEnd}
          sx={{
            "& .MuiDataGrid-cell": {
              cursor: "pointer",
            },
          }}
          pinnedColumns={pinnedColumns}
          onPinnedColumnsChange={(newPinned) => setPinnedColumns(newPinned)}
          onColumnWidthChange={(params) => handleColumnResize(params.width, params.colDef)}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          columnVisibilityModel={{ ...columnVisibilityModel, actions: showActionsColumn }}
        />
      </div>
      {selectedMessage && (
        <ReplyMessage
          conversationId={selectedMessage.conversationId}
          messageId={selectedMessage.messageId}
          mainSubject={selectedMessage.messageSubject}
          open={isModalOpen}
          onClose={handleModalClose}
          toUserId={
            type === ENTITYTYPE.INBOX ? selectedMessage.fromUserId : selectedMessage.toUserId
          }
          toUserName={
            type === ENTITYTYPE.INBOX
              ? (selectedMessage.cacheFromName ?? "")
              : updateTrailingNumber(selectedMessage.toCacheName, null, false)
          }
          type={type}
        />
      )}
      {reassignOpen && (
        <ReassignMessage
          open={reassignOpen}
          onClose={(success) => {
            setReassignOpen(false);
            if (success && selectedMessage) {
              setPageState((prev) => {
                if (!prev.data) return prev;
                const updatedData = prev.data.filter(
                  (m) => m.messageId !== selectedMessage.messageId,
                );
                return { data: updatedData, total: prev.total - 1 };
              });
            }
          }}
          messageId={selectedMessage?.messageId}
          currentAssigneeId={selectedMessage?.toUserId}
          recipientId={selectedMessage?.recipientID}
        />
      )}
      <ConfirmationDialog
        open={recallConfirm}
        title={ACTIONITEMVALIDATIONS.CONFIRM}
        message={INTERNALMESSAGES.RECALL_CONFIRM}
        onClose={handleRecallConfirm}
        onDiscard={handleDiscard}
      />
    </Box>
  );
};

export default InternalMessages;
